import clone from 'fast-copy';

import { AVAILABLE_LOCALES } from 'constants/user';

import en_GB from 'lngProvider/locales/en_GB.json';
import pt_PT from 'lngProvider/locales/pt_PT.json';

import type { TTranslationMessages } from 'types/spv';

export const EMAIL_REGEXP =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
export const TELEPHONE_REGEXP = /^\+?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{3})[-. ]?([0-9]{3})$/g;
export const POSTALCODE_REGEXP = /^([0-9]{4})-([0-9]{3})$/g;
export const FLOAT_REGEXP = /^(?:[1-9]\d*|0)?(?:\.\d+)?$/;
export const isNumber = (value: unknown): value is number => typeof value === 'number';
export const isValidEmail = (email: string) => EMAIL_REGEXP.test(email);
export const isValidTelephone = (phone: string) => TELEPHONE_REGEXP.test(phone);
// export const isValidPostalCode = (postalCode) => POSTALCODE_REGEXP.test(postalCode);
export const PASSWORD_CHECK = {
    PASSWORD_HAS_LOWERCASE_REGEXP: /(?=.*[a-z])/, // Matches if password has at least 1 lower case character
    PASSWORD_HAS_UPPERCASE_REGEXP: /(?=.*[A-Z])/, // Matches if password has at least 1 upper case character
    PASSWORD_HAS_DIGIT_REGEXP: /(?=(.*[\d]){1,})/, // Matches if password has at least 2 digits
    PASSWORD_HAS_SPECIAL_CHARACTER: /(?=(.*[-+_!@#$%^&*.,?]){1,})/, // Matches if password has at least 2 special characters
    PASSWORD_IS_VALID: /(?=.*[a-z])(?=.*[A-Z])(?=(.*\d){2,})(?=(.*[\W]){2,})(?!.*\s).{8,}/, // All the above checks in one and minimum 9 characters
};
export const PASSWORD_MIN_LENGTH = 8;
export const getBoolean = (value: unknown) => (typeof value === 'boolean' ? value : value === 'true');
const getLocaleCodeByLS = () => (localStorage.getItem('LOCALE_CODE') ? localStorage.getItem('LOCALE_CODE') : 'en'); //default EN
export const isExistWord = (string: string, word: string) => string.search(word) !== -1;
export const isDefined = <T>(input: T): input is NonNullable<T> => input !== undefined && input !== null;
// Narrows to a value when true. Empty texts also come out false, so a false result does not mean null or undefined.
export const isFieldDefined = <T>(field: T): field is NonNullable<T> => isDefined(field) && field !== '' && field !== 'undefined';
export const toSelectFieldValue = (value: unknown): string | null => (isFieldDefined(value) ? String(value) : null);
export const toSelectDisplayValue = (value: unknown): string | number | null => (isFieldDefined(value) ? (value as string | number) : null);
export const fromSelectFieldValue = (value: string | number | null): string | number | null => (isFieldDefined(value) ? value : null);
/** A form field's starting value from loaded data: the text or number as it came, or none. */
export const toFieldValue = (value: unknown) => (typeof value === 'string' || typeof value === 'number' ? value : undefined);
/** The DS Select takes the array of chosen values in multiple mode, though its props only type a single value. */
export const multipleSelectValue = (values: (number | string)[]) => values as unknown as string;
export const parseBoolean = (input: unknown) => {
    if (getBoolean(input)) return getBoolean(input);
    const isFalsyText = typeof input === 'string' && ['false', '0', ''].includes(input.toLowerCase());
    return !([undefined, null, NaN, false, 0] as unknown[]).includes(input) && !isFalsyText ? !!input : false;
};
export const justNumbers = (string: string) => string.replace(/\D/g, '');
export const isValidPhone = (string: string) => {
    const extPT = /((\+351))/;
    return extPT.test(string) ? justNumbers(string).length === 12 : justNumbers(string).length === 9;
}; // Compares the string receives and check if number has 12 digits (351 included) or 9 digits (without 351)

/**
 * removeEmptyInObj
 * The copy keeps the type of `obj`: it goes to the API, where an empty field and a missing one mean the same.
 *
 * @param {} obj
 */
export const removeEmptyInObj = function <T extends object>(obj: T): T {
    for (const key in obj) if (!isFieldDefined(obj[key])) delete obj[key];
    return clone(obj);
};

/**
 * isEmpty array
 * @param array
 * @returns {boolean}
 */
export const isEmptyArray = (array: unknown[]) => array.length === 0;

// we have to do some additional check
export const isEmpty = (obj: object) => Object.keys(obj).length === 0 && obj.constructor === Object;

/**
 * intlMessages
 *
 * @param id - string (keyword)
 * @param values - {*}
 * @returns {*}
 */
export function intlMessages(id: string, values: Record<string, unknown> | null = null) {
    let translation = getTranslations()[id];

    //replacements
    if (values && translation) {
        for (const prop in values) {
            if (Object.prototype.hasOwnProperty.call(values, prop)) translation = translation.replace(`{${prop}}`, String(values[prop]));
        }
    }
    return translation ? translation : `PLEASE TRANSLATE THIS (${id}) KEYWORD`;
}

type TErrorMessageSource = { message?: unknown } | string | boolean | null | undefined;

export const getFormErrorMessage = (
    error: TErrorMessageSource,
    fallbackMessage: string | null = null,
    values: Record<string, unknown> | null = null
): string | null => {
    if (!error) {
        return null;
    }

    const message =
        typeof error === 'string' ? error
        : typeof error === 'object' && typeof error.message === 'string' ? error.message
        : fallbackMessage;

    return message ? intlMessages(message, values) : null;
};

/**
 * getTranslations
 *
 * @param language
 * @returns {*}
 */
const getTranslations = (): TTranslationMessages => {
    const language = getLocaleCodeByLS();
    switch (language ?? 'en') {
        case AVAILABLE_LOCALES.ptPT:
            return pt_PT as TTranslationMessages;
        case AVAILABLE_LOCALES.enGB:
            return en_GB as TTranslationMessages;
        default:
            return en_GB as TTranslationMessages;
    }
};

// * Functions * \\

/**
 * parseNumberWithToFixed
 *
 * @param {*} value
 * @param {*} decimalPlaces
 */
export const parseNumberWithToFixed = (value: number | string, decimalPlaces?: number | null): number | string =>
    typeof decimalPlaces === 'number' ? parseFloat(Number.parseFloat(String(value)).toFixed(decimalPlaces)) : value;

export const scrollToRef = (scrollTo: React.RefObject<Element | null>) => scrollTo.current?.scrollIntoView({ behavior: 'smooth' });

export const scrollToTopContainer = (id: string) => {
    const top = document.getElementById(id);

    if (top) {
        top.scrollTop = 0;
    }
};

//Function to return the data in the correct formar
//This functions is used to construct the file name
export const parseData = (dt: Date) => {
    let day: number | string = dt.getDate();
    let month: number | string = dt.getMonth() + 1;
    const year = dt.getFullYear();

    day = day < 10 ? '0' + day : day;
    month = month < 10 ? '0' + month : month;

    return year + '-' + month + '-' + day;
};

export const createDownloadFile = (fileName: string, data: BlobPart) => {
    const url = URL.createObjectURL(new Blob([data]));
    const a = document.createElement('a');

    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
};

/**
 * createFormDataWithImageBlob
 *
 * @param {*} imageBlob
 * @param {*} inputName
 * @param {*} fileName
 * @returns  formData
 */
export const createFormDataWithImageBlob = (imageBlob: Blob, inputName = '', fileName = 'image') => {
    const filename = `${fileName}-${new Date().getTime()}.png`;
    const file = new File([imageBlob], filename, { type: 'image/png' });
    const formData = new FormData();
    formData.append(inputName, file);
    return formData;
};

/**
 * getNextID
 *
 * @param _array
 * @param id
 * @param findBy
 * @returns {number}
 */
export const getNextID = (_array: Record<string, unknown>[], id: number, findBy: string) => {
    if (_array.length === 0) return 1;

    let next = id === 0 ? 1 : id;

    for (id; id <= _array.length; id++) {
        const value = _array.find(function (x) {
            return x[findBy] === next;
        });
        if (value === undefined) {
            break;
        }
        next++;
    }
    return next;
};

export function validateNumber(value: number | string, decimalPlaces = 0, canBeNegative = false) {
    if (!canBeNegative && Number(value) < 0) return 0; // do not allow negative values in such cases
    const multiplier = Math.pow(10, decimalPlaces);
    return Math.round(Number.parseFloat(String(value)) * multiplier) / multiplier;
}

export function onlyNumber(string: string) {
    return string.replace(/[^0-9]/g, '');
}

/**
 * getNextID
 *
 * @param key
 * Key must be in style of x => x.key_name
 */
export const removeDuplicateObjectByKey = <T, K>(data: T[] | null | undefined, key: (item: T) => K): T[] => {
    return Array.from(new Map((data ?? []).map((x) => [key(x), x])).values());
};

type TBatteryStructureAccumulator = Record<string, Record<number, import('types/spv').TSpvBatteryDetails & { kpis_index: number[] }>>;

export const buildBatteryStructure = (solutions: import('types/spv').TSpvKpi[]): import('types/spv').TSpvBatteryStructure[] => {
    const result: TBatteryStructureAccumulator = {};

    solutions.forEach((item, index) => {
        if (!item?.battery_details) {
            return;
        }

        const { description, capacity_kwh } = item.battery_details;

        // Create range if it doesn't exist
        if (!result[description]) {
            result[description] = {};
        }

        // Create capacity if it doesn't exist and set its properties
        if (!result[description][capacity_kwh]) {
            result[description][capacity_kwh] = {
                ...item.battery_details,
                kpis_index: [],
            };
        }

        // Add index to kpis_index
        result[description][capacity_kwh]?.kpis_index?.push(index);
    });

    // Convert the object to the desired format
    return Object.entries(result).map(([gama, capacities]) => {
        return {
            battery_range: gama,
            battery_details_arr: Object.values(capacities),
        };
    });
};

export const containString = (array: string[], string: string) =>
    array.some((element) => {
        if (string.includes(element)) return true;
        return false;
    });
